import { useCallback, useEffect, useState } from 'react';
import { EXAM_TYPE_OPTIONS, DEFAULT_EXAM_TYPE } from '../components/clinical/clinicalOptions.js';

// Frontend-only persistence for the user's chosen study exam type.
//
// This is the exam type used by exam GENERATION (POST /api/exams → clinicals engine).
// We deliberately keep it client-side (localStorage) per product decision — no backend
// storage. A tiny module-level subscriber store keeps every mounted consumer in sync
// within the tab (e.g. changing it on the Account page updates the value the exam-config
// modal will default to), and the `storage` event keeps other tabs in sync.

const STORAGE_KEY = 'ff.studyExamType';

const VALID_VALUES = new Set(EXAM_TYPE_OPTIONS.map((o) => o.value));

function isValid(value) {
    return typeof value === 'string' && VALID_VALUES.has(value);
}

function readStored() {
    try {
        const v = localStorage.getItem(STORAGE_KEY);
        return isValid(v) ? v : DEFAULT_EXAM_TYPE;
    } catch (_) {
        return DEFAULT_EXAM_TYPE;
    }
}

const subscribers = new Set();
let current = readStored();

function setStudyExamTypeGlobal(value) {
    if (!isValid(value) || value === current) return;
    current = value;
    try { localStorage.setItem(STORAGE_KEY, value); } catch (_) {}
    subscribers.forEach((fn) => {
        try { fn(value); } catch (_) {}
    });
}

/**
 * @returns {[string, (value: string) => void]} [studyExamType, setStudyExamType]
 */
export function useStudyExamType() {
    const [value, setValue] = useState(current);

    useEffect(() => {
        // Sync to the latest stored value on mount (covers writes that happened while
        // this component was unmounted), then subscribe to live updates.
        setValue(current);
        subscribers.add(setValue);

        const onStorage = (e) => {
            if (e.key === STORAGE_KEY && isValid(e.newValue) && e.newValue !== current) {
                current = e.newValue;
                subscribers.forEach((fn) => { try { fn(e.newValue); } catch (_) {} });
            }
        };
        window.addEventListener('storage', onStorage);

        return () => {
            subscribers.delete(setValue);
            window.removeEventListener('storage', onStorage);
        };
    }, []);

    const set = useCallback((next) => setStudyExamTypeGlobal(next), []);

    return [value, set];
}

export default useStudyExamType;
